import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import AppShell, { CartButton } from './AppShell';
import SepayModal from './SepayModal';
import Reveal from './Reveal';
import useCart, { clearCart } from '../hooks/useCart';
import useOrderStatus from '../hooks/useOrderStatus';
import {
    API_BASE, getDeliveryDates, formatCurrency, formatDateShort,
    TIME_SLOTS, getImageUrl, publicImg,
} from '../config/api';
import './CartPage.css';

const EARLY_DISCOUNT = 0; // Đã tắt ưu đãi đặt sớm

export default function CartPage() {
    const navigate = useNavigate();
    const dates = getDeliveryDates();
    const { items, setItems, count } = useCart();
    const { orderStatus, orderEnabled } = useOrderStatus();

    const [deliveryDate, setDeliveryDate] = useState(() => {
        const saved = localStorage.getItem('selectedDeliveryDate');
        return dates.some(d => d.iso === saved) ? saved : (dates[0]?.iso || '2026-09-14');
    });
    const [deliverySlot, setDeliverySlot] = useState(
        () => localStorage.getItem('selectedDeliverySlot') || TIME_SLOTS[0]
    );

    const [name, setName] = useState(() => localStorage.getItem('customerName') || '');
    const [phone, setPhone] = useState(() => localStorage.getItem('customerPhone') || '');
    const [address, setAddress] = useState(() => localStorage.getItem('customerAddress') || '');
    const [note, setNote] = useState('');

    const [payment, setPayment] = useState('vietqr');
    const [submitting, setSubmitting] = useState(false);
    const [bill, setBill] = useState(null);
    const [showPay, setShowPay] = useState(false);

    useEffect(() => {
        localStorage.setItem('selectedDeliveryDate', deliveryDate);
        localStorage.setItem('selectedDeliverySlot', deliverySlot);
    }, [deliveryDate, deliverySlot]);

    const subtotal = items.reduce((s, it) => s + (Number(it.total) || 0), 0);
    const shipping = 0; // Freeship mọi đơn hàng
    const discount = subtotal > 0 ? Math.round(subtotal * EARLY_DISCOUNT) : 0;
    const grandTotal = Math.max(0, subtotal + shipping - discount);

    const changeQty = (idx, delta) => {
        const next = [...items];
        const item = { ...next[idx] };
        const qty = (Number(item.qty) || 1) + delta;
        if (qty <= 0) { removeItem(idx); return; }
        item.qty = qty;
        item.total = qty * (Number(item.price) || 0);
        next[idx] = item;
        setItems(next);
    };

    const removeItem = (idx) => {
        const item = items[idx];
        Swal.fire({
            title: 'Bỏ món này?',
            text: `"${item.name}" sẽ được xoá khỏi giỏ hàng.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#D32F2F',
            cancelButtonColor: '#7A6A5F',
            confirmButtonText: 'Xoá',
            cancelButtonText: 'Giữ lại',
        }).then((res) => {
            if (res.isConfirmed) setItems(items.filter((_, i) => i !== idx));
        });
    };

    const warn = (title, text) =>
        Swal.fire({ icon: 'warning', title, text, confirmButtonColor: '#142C54' });

    const submitOrder = async (opts = {}) => {
        if (!orderEnabled) {
            return Swal.fire({
                icon: 'info',
                title: orderStatus.closedTitle || 'Tiệm tạm ngưng nhận đơn',
                html: `
                    <div style="text-align: left; padding: 4px 6px; font-size: 14px; line-height: 1.6; color: #334155;">
                        <p style="margin-bottom: 12px;">${orderStatus.closedReason || 'Tiệm hiện đang tạm dừng nhận đơn mới. Quý khách vui lòng quay lại sau!'}</p>
                        ${orderStatus.reopenTime ? `<div style="background: #F8FAFC; border-left: 3px solid #142C54; padding: 8px 12px; margin-bottom: 12px; border-radius: 4px; font-size: 13px;"><strong>Thời gian:</strong> ${orderStatus.reopenTime}</div>` : ''}
                        ${orderStatus.hotline ? `<p style="margin-bottom: 0; font-size: 13.5px;">Hotline hỗ trợ: <strong style="color: #B8862F;">${orderStatus.hotline}</strong></p>` : ''}
                    </div>
                `,
                confirmButtonColor: '#142C54',
                confirmButtonText: 'Đã hiểu'
            });
        }
        if (items.length === 0) return warn('Giỏ hàng trống', 'Hãy chọn bánh vào giỏ trước khi đặt nhé!');
        if (!name.trim()) return warn('Thiếu họ tên', 'Vui lòng nhập họ tên người nhận bánh.');
        if (phone.trim().length < 8) return warn('Thiếu số điện thoại', 'Vui lòng nhập số điện thoại hợp lệ để giao hàng.');
        if (!address.trim()) return warn('Thiếu địa chỉ', 'Vui lòng nhập địa chỉ nhận bánh.');

        localStorage.setItem('customerName', name.trim());
        localStorage.setItem('customerPhone', phone.trim());
        localStorage.setItem('customerAddress', address.trim());

        const isPayLater = opts.payLater !== undefined ? opts.payLater : payment === 'pay_later';
        const methodToSend = isPayLater ? 'pay_later' : (opts.payNow ? 'vietqr' : payment);

        setSubmitting(true);
        try {
            const res = await axios.post(`${API_BASE}/bill/addNewBill`, {
                customerName: name.trim(),
                customerPhone: phone.trim(),
                customerAddress: address.trim(),
                delivery_date: deliveryDate,
                deliveryTimeSlot: deliverySlot,
                comment_user: note.trim(),
                items,
                totalAmount: grandTotal,
                paymentMethod: methodToSend,
            });

            if (res.data?.bill) {
                const created = res.data.bill;
                clearCart();
                localStorage.setItem('lastOrderCode', created.orderCode);

                if (isPayLater) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Đặt bánh thành công!',
                        html: `
                            <div style="text-align: left; padding: 4px 6px; font-size: 14px; line-height: 1.6;">
                                <p style="margin-bottom: 8px;">Mã đơn: <strong style="color: #142C54; font-size: 16px;">#${created.orderCode}</strong></p>
                                <p style="margin-bottom: 10px; color: #475569;">Đơn hàng đã được lưu thành công vào hệ thống tiệm bánh.</p>
                                <div style="background: #F8FAFC; border: 1px dashed #CBD5E1; border-radius: 8px; padding: 12px; margin-bottom: 6px; font-size: 13px; color: #334155;">
                                    <div style="display: flex; gap: 8px; align-items: flex-start;">
                                        <i class="fa-regular fa-clock" style="color: #142C54; margin-top: 2px; font-size: 16px;"></i>
                                        <div>
                                            <strong>Chưa cần thanh toán ngay:</strong><br/>
                                            Đơn được lưu ở trạng thái <em>Chờ thanh toán</em>. Bạn có thể thanh toán sau bất kỳ lúc nào trong mục <strong>"Đơn của tôi"</strong> hoặc khi nhận bánh.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `,
                        confirmButtonColor: '#142C54',
                        confirmButtonText: 'Xem đơn của tôi',
                        showCancelButton: true,
                        cancelButtonText: 'Tiếp tục xem menu',
                        cancelButtonColor: '#7A6A5F',
                    }).then((result) => {
                        if (result.isConfirmed) {
                            navigate('/my-orders');
                        } else {
                            navigate('/menu');
                        }
                    });
                } else {
                    setBill(created);
                    setShowPay(true);
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Không tạo được đơn',
                    text: res.data?.message || 'Có lỗi xảy ra, vui lòng thử lại.',
                    confirmButtonColor: '#142C54',
                });
            }
        } catch (err) {
            console.error('Lỗi đặt hàng:', err);
            Swal.fire({
                icon: 'error',
                title: 'Lỗi máy chủ',
                text: err.response?.data?.message || 'Không thể kết nối máy chủ. Vui lòng thử lại!',
                confirmButtonColor: '#142C54',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const onPaid = (paidBill) => {
        setShowPay(false);
        Swal.fire({
            icon: 'success',
            title: 'Thanh toán thành công!',
            text: `Đơn #${paidBill.orderCode} đã được ghi nhận. Bếp sẽ chuẩn bị bánh và giao đúng hẹn.`,
            confirmButtonColor: '#142C54',
            confirmButtonText: 'Xem đơn hàng',
        }).then(() => navigate('/my-orders'));
    };

    const itemImage = (item) => {
        if (item.isBox) {
            const fallback = item.capacity === 2 || (item.name || '').includes('2')
                ? '/imgs/hop-2-banh.webp'
                : '/imgs/hop-4-banh.webp';
            return publicImg(item.boxImage || item.imageUrl || item.img || fallback);
        }
        return getImageUrl(item.imageUrl);
    };

    const empty = items.length === 0;

    return (
        <AppShell
            title="Giỏ hàng"
            subtitle={empty ? 'Chưa có món nào' : `${count} món · ${formatCurrency(grandTotal)}`}
            back
            onBack={() => navigate('/menu')}
            crumbs={[{ label: 'Giỏ hàng & đặt bánh' }]}
            right={<CartButton />}
            className="no-desk-bar"
            actionBar={empty ? null : (
                <>
                    <div className="bar-total">
                        <div className="bar-total-label">Tổng cộng</div>
                        <div className="bar-total-value tick" key={grandTotal}>{formatCurrency(grandTotal)}</div>
                    </div>
                    {!orderEnabled ? (
                        <button
                            type="button"
                            className="btn btn-sm btn-order-paused"
                            style={{ flex: 1, padding: '9px 12px', fontSize: '13px' }}
                            onClick={() => submitOrder()}
                        >
                            <i className="fa-solid fa-pause-circle" style={{ marginRight: 6 }} /> Tiệm tạm ngưng nhận đơn
                        </button>
                    ) : (
                        <div className="bar-btns-group">
                            <button
                                type="button"
                                className={`btn btn-sm bar-btn-later ${payment === 'pay_later' ? 'btn-primary' : 'btn-outline'}`}
                                disabled={submitting}
                                onClick={() => submitOrder({ payLater: true })}
                            >
                                {submitting && payment === 'pay_later' ? (
                                    <><i className="fa-solid fa-spinner spin" /> Đang tạo...</>
                                ) : (
                                    <>Thanh toán sau</>
                                )}
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm bar-btn-now ${payment === 'pay_later' ? 'btn-outline' : 'btn-primary'}`}
                                disabled={submitting}
                                onClick={() => submitOrder({ payNow: true })}
                            >
                                {submitting && payment !== 'pay_later' ? (
                                    <><i className="fa-solid fa-spinner spin" /></>
                                ) : (
                                    <><i className="fa-solid fa-qrcode" style={{ fontSize: 11, marginRight: 4 }} /> Quét QR</>
                                )}
                            </button>
                        </div>
                    )}
                </>
            )}
        >
            {empty ? (
                <div className="shell">
                    <div className="empty" style={{ paddingTop: 60 }}>
                        <div className="empty-icon"><i className="fa-solid fa-basket-shopping" /></div>
                        <h3>Giỏ hàng đang trống</h3>
                        <p>Hãy chọn những hộp bánh trung thu handmade cho mùa trăng này nhé!</p>
                        <Link to="/menu" className="btn btn-primary">Khám phá menu bánh</Link>
                    </div>
                </div>
            ) : (
                <div className="shell checkout-grid">
                    <div className="checkout-main">
                        {!orderEnabled && (
                            <div className="order-paused-banner">
                                <div className="order-paused-header">
                                    <div className="order-paused-badge">
                                        <span className="paused-pulse-dot" />
                                        <span>TẠM DỪNG NHẬN ĐƠN MỚI</span>
                                    </div>
                                    <div className="order-paused-tag-sub">Thông báo chính thức từ tiệm</div>
                                </div>
                                <h3 className="order-paused-title">
                                    <i className="fa-solid fa-store-slash order-paused-icon" />
                                    {orderStatus.closedTitle || 'Tiệm Tạm Dừng Nhận Đơn Mới'}
                                </h3>
                                <p className="order-paused-reason">
                                    {orderStatus.closedReason || 'Để chuẩn bị chu đáo và đảm bảo chất lượng từng mẻ bánh tươi ngon nhất phục vụ quý khách, tiệm xin phép tạm ngưng nhận thêm đơn mới vào lúc này. Rất mong quý khách thông cảm!'}
                                </p>
                                {orderStatus.reopenTime && (
                                    <div className="order-paused-time">
                                        <i className="fa-regular fa-clock" />
                                        <span><strong>Dự kiến:</strong> {orderStatus.reopenTime}</span>
                                    </div>
                                )}
                                <div className="order-paused-actions">
                                    {orderStatus.hotline && (
                                        <a href={`tel:${orderStatus.hotline.replace(/\D/g, '')}`} className="btn-paused-hotline">
                                            <i className="fa-solid fa-phone" />
                                            <span>Hotline: {orderStatus.hotline}</span>
                                        </a>
                                    )}
                                    {orderStatus.zaloLink && (
                                        <a href={orderStatus.zaloLink} target="_blank" rel="noopener noreferrer" className="btn-paused-zalo">
                                            <i className="fa-solid fa-comment-dots" />
                                            <span>Nhắn tin Zalo</span>
                                        </a>
                                    )}
                                    <Link to="/menu" className="btn-paused-menu">
                                        <i className="fa-solid fa-cookie-bite" />
                                        <span>Xem menu bánh</span>
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* ITEMS */}
                        <section>
                            <div className="section-head" style={{ marginTop: 12 }}>
                                <h2 className="h-section">Đơn của bạn</h2>
                                <span className="t-muted">{items.length} mục</span>
                            </div>

                            <Reveal stagger className="cart-list">
                                {items.map((item, idx) => (
                                    <div key={item.id || idx} className="cart-item">
                                        <img
                                            className="cart-item-img img-fade"
                                            onLoad={(e) => e.currentTarget.classList.add('loaded')}
                                            src={itemImage(item)}
                                            alt={item.name}
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = publicImg(item.isBox ? '/imgs/hop-4-banh.webp' : '/imgs/banh-trung-thu-thap-cam-lap-xuong.jpg');
                                            }}
                                        />

                                        <div className="cart-item-main">
                                            <div className="cart-item-top">
                                                <div className="cart-item-name">{item.name}</div>
                                                <button type="button" className="cart-item-del" onClick={() => removeItem(idx)} aria-label="Xoá món">
                                                    <i className="fa-regular fa-trash-can" />
                                                </button>
                                            </div>

                                            <div className="cart-item-desc">
                                                {item.itemsSummary || (item.isBox ? '' : `${item.weight || '150g'} · bánh lẻ`)}
                                            </div>

                                            {item.isBox ? (
                                                <div className="cart-item-free"><i className="fa-solid fa-gift" /> Miễn phí vỏ hộp &amp; túi xách</div>
                                            ) : (
                                                <div className="cart-item-free" style={{ color: 'var(--muted)' }}><i className="fa-solid fa-box-open" /> Đóng túi giữ ẩm · Không kèm hộp quà</div>
                                            )}

                                            <div className="cart-item-bottom">
                                                <div className="stepper">
                                                    <button type="button" onClick={() => changeQty(idx, -1)} aria-label="Giảm">
                                                        <i className="fa-solid fa-minus" />
                                                    </button>
                                                    <span className="n">{item.qty}</span>
                                                    <button type="button" onClick={() => changeQty(idx, 1)} aria-label="Tăng">
                                                        <i className="fa-solid fa-plus" />
                                                    </button>
                                                </div>
                                                <div className="cart-item-price">{formatCurrency(item.total)}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </Reveal>
                        </section>

                        {/* DELIVERY */}
                        <section>
                            <div className="section-head">
                                <h2 className="h-section">Ngày nhận bánh</h2>
                                <span className="t-muted">3 đợt giao: 14/09, 18/09 &amp; 23/09</span>
                            </div>

                            <div className="dates-2-grid">
                                {dates.map((d) => (
                                    <button
                                        key={d.iso}
                                        type="button"
                                        className={`daychip-wide ${d.iso === deliveryDate ? 'on' : ''}`}
                                        onClick={() => setDeliveryDate(d.iso)}
                                    >
                                        <div className="daychip-dow">{d.dowFull || (d.dow === 'T2' ? 'Thứ Hai' : 'Thứ Sáu')}</div>
                                        <div className="daychip-date">{d.day} Tháng 09</div>
                                        <div className="daychip-note">{d.iso === deliveryDate ? 'Đang chọn' : d.note}</div>
                                    </button>
                                ))}
                            </div>

                            <div className="sheet-label">Khung giờ nhận</div>
                            <div className="slot-grid">
                                {TIME_SLOTS.map((s) => (
                                    <button key={s} type="button" className={`slot ${deliverySlot === s ? 'on' : ''}`} onClick={() => setDeliverySlot(s)}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </section>

                        {/* RECIPIENT */}
                        <section>
                            <div className="section-head">
                                <h2 className="h-section">Người nhận</h2>
                            </div>

                            <div className="card card-pad">
                                <div className="field">
                                    <label htmlFor="cust-name">Họ tên <span className="req">*</span></label>
                                    <input id="cust-name" className="input" type="text" autoComplete="name"
                                        placeholder="Ví dụ: Nguyễn Văn A" value={name} onChange={(e) => setName(e.target.value)} />
                                </div>

                                <div className="field">
                                    <label htmlFor="cust-phone">Số điện thoại <span className="req">*</span></label>
                                    <input id="cust-phone" className="input" type="tel" inputMode="tel" autoComplete="tel"
                                        placeholder="0856 345 789" value={phone} onChange={(e) => setPhone(e.target.value)} />
                                </div>

                                <div className="field">
                                    <label htmlFor="cust-addr">Địa chỉ nhận bánh <span className="req">*</span></label>
                                    <input id="cust-addr" className="input" type="text" autoComplete="street-address"
                                        placeholder="Số nhà, đường, phường/xã, quận/huyện" value={address} onChange={(e) => setAddress(e.target.value)} />
                                </div>

                                <div className="field" style={{ marginBottom: 0 }}>
                                    <label htmlFor="cust-note">Ghi chú cho xưởng &amp; shipper</label>
                                    <textarea id="cust-note" className="input" rows="2"
                                        placeholder="Gọi trước 15 phút, gửi bảo vệ toà nhà, bánh ít ngọt..."
                                        value={note} onChange={(e) => setNote(e.target.value)} />
                                </div>
                            </div>
                        </section>
                    </div>

                    <div className="checkout-side">
                        {/* PAYMENT */}
                        <section>
                            <div className="section-head">
                                <h2 className="h-section">Thanh toán</h2>
                            </div>

                            <button type="button" className={`pay-opt ${payment === 'vietqr' ? 'on' : ''}`} onClick={() => setPayment('vietqr')}>
                                <span className="radio" />
                                <span className="pay-opt-text">
                                    <span className="pay-opt-name">Chuyển khoản VietQR</span>
                                    <span className="pay-opt-sub">Quét mã trên app ngân hàng · xác nhận tức thì</span>
                                </span>
                                <i className="fa-solid fa-qrcode" />
                            </button>

                            <button type="button" className={`pay-opt ${payment === 'pay_later' ? 'on' : ''}`} onClick={() => setPayment('pay_later')}>
                                <span className="radio" />
                                <span className="pay-opt-text">
                                    <span className="pay-opt-name">Thanh toán sau (Chưa cần trả ngay)</span>
                                    <span className="pay-opt-sub">Đặt bánh giữ chỗ · Thanh toán sau trong "Đơn của tôi" hoặc khi nhận bánh</span>
                                </span>
                                <i className="fa-regular fa-clock" />
                            </button>

                            <button type="button" className={`pay-opt ${payment === 'deposit_30' ? 'on' : ''}`} onClick={() => setPayment('deposit_30')}>
                                <span className="radio" />
                                <span className="pay-opt-text">
                                    <span className="pay-opt-name">Cọc 30%, trả nốt khi nhận</span>
                                    <span className="pay-opt-sub">Áp dụng cho đơn từ 1.000.000 ₫</span>
                                </span>
                                <i className="fa-solid fa-hand-holding-dollar" />
                            </button>
                        </section>

                        {/* SUMMARY */}
                        <section>
                            <div className="summary card">
                                <div className="summary-deliv">
                                    <div className="eyebrow">Giao đúng hẹn</div>
                                    <div className="summary-date">{formatDateShort(deliveryDate)}</div>
                                    <div className="t-muted">Khung giờ {deliverySlot}</div>
                                    {address && <div className="summary-addr clamp-1">Đến: {address}</div>}
                                </div>

                                <div className="summary-rows">
                                    <div className="srow">
                                        <span>Tiền bánh ({items.length} mục)</span>
                                        <span>{formatCurrency(subtotal)}</span>
                                    </div>
                                    <div className="srow">
                                        <span>Phí giao hàng</span>
                                        <span className="ok">
                                            <i className="fa-solid fa-gift" style={{ marginRight: 4, fontSize: 11 }} /> Miễn phí (Freeship)
                                        </span>
                                    </div>
                                    {discount > 0 && (
                                        <div className="srow">
                                            <span>Ưu đãi đặt sớm (−5%)</span>
                                            <span className="minus">−{formatCurrency(discount)}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="summary-total">
                                    <span>Tổng cộng</span>
                                    <strong className="tick" key={grandTotal}>{formatCurrency(grandTotal)}</strong>
                                </div>

                                <div className="summary-cta desk-only">
                                    {!orderEnabled ? (
                                        <div className="order-paused-summary-box">
                                            <button
                                                type="button"
                                                className="btn btn-order-paused btn-block btn-lg"
                                                onClick={() => submitOrder()}
                                            >
                                                <i className="fa-solid fa-pause-circle" style={{ marginRight: 8 }} />
                                                Tiệm đang tạm ngưng nhận đơn
                                            </button>
                                            <div className="order-paused-hint">
                                                <i className="fa-solid fa-circle-info" /> Đơn tạm thời chưa thể gửi đi. Quý khách có thể liên hệ hotline để được phục vụ nhanh.
                                            </div>
                                        </div>
                                    ) : payment === 'pay_later' ? (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-block btn-lg"
                                                disabled={submitting}
                                                onClick={() => submitOrder({ payLater: true })}
                                            >
                                                {submitting ? (
                                                    <><i className="fa-solid fa-spinner spin" /> Đang tạo đơn...</>
                                                ) : (
                                                    <><i className="fa-solid fa-check" style={{ marginRight: 6 }} /> Đặt bánh (Thanh toán sau) <i className="fa-solid fa-arrow-right" style={{ fontSize: 12, marginLeft: 4 }} /></>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline btn-block"
                                                style={{ marginTop: 8 }}
                                                disabled={submitting}
                                                onClick={() => submitOrder({ payNow: true })}
                                            >
                                                <i className="fa-solid fa-qrcode" style={{ marginRight: 6 }} /> Đổi sang thanh toán ngay bằng VietQR
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-block btn-lg"
                                                disabled={submitting}
                                                onClick={() => submitOrder({ payNow: true })}
                                            >
                                                {submitting ? (
                                                    <><i className="fa-solid fa-spinner spin" /> Đang tạo đơn...</>
                                                ) : (
                                                    <><i className="fa-solid fa-qrcode" style={{ marginRight: 6 }} /> Đặt &amp; Quét mã VietQR <i className="fa-solid fa-arrow-right" style={{ fontSize: 12, marginLeft: 4 }} /></>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline btn-block"
                                                style={{ marginTop: 8 }}
                                                disabled={submitting}
                                                onClick={() => submitOrder({ payLater: true })}
                                            >
                                                <i className="fa-regular fa-clock" style={{ marginRight: 6 }} /> Đặt trước · Thanh toán sau
                                            </button>
                                        </>
                                    )}
                                </div>

                                <div className="summary-note">
                                    Đơn được giữ chỗ chờ thanh toán · Đổi ngày giao miễn phí trước 48 giờ.
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            )}

            {bill && (
                <SepayModal
                    show={showPay}
                    bill={bill}
                    onClose={() => {
                        setShowPay(false);
                        Swal.fire({
                            icon: 'info',
                            title: 'Đã lưu đơn hàng',
                            html: `Đơn hàng <strong>#${bill.orderCode}</strong> đã được ghi nhận.<br/>Bạn có thể thanh toán sau trong mục <strong>Đơn của tôi</strong> bất kỳ lúc nào.`,
                            confirmButtonColor: '#142C54',
                            confirmButtonText: 'Xem đơn của tôi',
                            showCancelButton: true,
                            cancelButtonText: 'Về trang chủ',
                            cancelButtonColor: '#7A6A5F',
                        }).then((res) => {
                            if (res.isConfirmed) navigate('/my-orders');
                            else navigate('/');
                        });
                    }}
                    onPaymentSuccess={onPaid}
                />
            )}
        </AppShell>
    );
}
